import { ClipboardButton } from '@calendly/clipboard';
import { I18n } from '@calendly/i18n';
import { Row, Cell as MobileCell } from '@calendly/table';
import { BareButton } from '@calendly/ui/components/bare-button';
import { Button } from '@calendly/ui/components/button';
import { Icon } from '@calendly/ui/components/icon';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { range } from 'lodash-es';
import moment from 'moment-timezone';
import { forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CopyText } from 'features/organization_settings/shared/styles';
import { useBooleanState } from 'hooks/useBooleanState';
import { useToggleState } from 'hooks/useToggleState';
import { timezoneSelector } from 'selectors/current_user';
import { formatDate } from 'services/time_format';
import { destroyBearerToken } from '../thunks';
import { RevokeTokenPopup } from './revoke_token_popup';
const dangerButtonRedCss = "d1n0y28a";
const dangerButtonCss = "d1t0gtak";
const DangerButton = forwardRef((props, ref) => {
  const decoration = 'minimal';
  const fontSize = 'medium';
  const red = true;
  return <Button ref={ref} {...props} decoration={decoration} fontSize={fontSize} className={classNames(dangerButtonCss, {
    [dangerButtonRedCss]: red
  }, props.className)} />;
});
const tokenSectionCss = "t1eldpfn";
const TokenSection = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(tokenSectionCss, props.className)} />;
});
const largeIconCss = "l1j4irgt";
const LargeIcon = forwardRef((props, ref) => <Icon ref={ref} {...props} className={classNames(largeIconCss, props.className)} />);
const themedIconCss = "t1pqy9p4";
const ThemedIcon = forwardRef((props, ref) => <LargeIcon ref={ref} {...props} className={classNames(themedIconCss, props.className)} />);
const maskCss = "m3gsl01";
const Mask = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'span';
  return <Component ref={ref} {...restProps} className={classNames(maskCss, props.className)} />;
});
const actionsIsDesktopUpCss = "a1vnl6jg";
const actionsIsPhoneCss = "a1cpwbqf";
const actionsCss = "a18xngrc";
const Actions = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  const isDesktopUp = useDeviceType('isDesktopUp');
  return <Component ref={ref} {...restProps} className={classNames(actionsCss, {
    [actionsIsDesktopUpCss]: isDesktopUp,
    [actionsIsPhoneCss]: isPhone
  }, props.className)} />;
});
const TokenMask = props => <Mask {...props}>{range(12).map(i => <ThemedIcon key={i} name="dot" />)}</Mask>;
const desktopCellHasBorderCss = "d10vksyx";
const desktopCellCss = "dojg66p";
const DesktopCell = forwardRef((props, ref) => {
  const {
    hasBorder,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'td';
  return <Component ref={ref} {...restProps} className={classNames(desktopCellCss, {
    [desktopCellHasBorderCss]: hasBorder
  }, props.className)} />;
});
const Cell = ({
  hasBorder,
  children,
  ...props
}) => useDeviceType('isDesktopUp') ? <DesktopCell hasBorder={hasBorder}>{children}</DesktopCell> : <MobileCell {...props}>{children}</MobileCell>;
const Token = ({
  bearerToken
}) => {
  const [isTokenVisible, toggleTokenVisible] = useToggleState(false);
  return <TokenSection>
      <BareButton tooltip={isTokenVisible ? 'Bearer token visible' : 'Bearer token hidden'} onClick={toggleTokenVisible}>
        <LargeIcon name={isTokenVisible ? 'visible-bold' : 'secret-bold'} />
      </BareButton>
      {isTokenVisible ? <CopyText data-component="bearer-token">{bearerToken.token}</CopyText> : <TokenMask data-component="masked-bearer-token" />}
    </TokenSection>;
};
export const TokenRow = ({
  bearerToken
}) => {
  const dispatch = useDispatch();
  const timezone = useSelector(timezoneSelector);
  const [isOpen, openPopup, closePopup] = useBooleanState(false);
  const hasToken = Boolean(bearerToken.token);
  const isDesktopUp = useDeviceType('isDesktopUp');
  const handleRevoke = () => dispatch(destroyBearerToken({
    token: bearerToken,
    callback: closePopup
  }));
  const formatTokenDate = dateTime => {
    const momentTime = moment(dateTime).tz(timezone);
    return `${formatDate(momentTime, '%b %-d, %Y')} @ ${formatDate(momentTime, '%l:%M%P %Z')}`;
  };
  return <>
      <Row>
        <Cell label={I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.bearer_token.generated_on')} hasBorder={!hasToken}>
          {formatTokenDate(bearerToken.created_at)}
        </Cell>
        <Cell label={I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.bearer_token.expires_at')} hasBorder={!hasToken}>
          {bearerToken.expires_at ? formatTokenDate(bearerToken.expires_at) : I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.token_durations.durations.never')}
        </Cell>
        {hasToken && !isDesktopUp && <Cell label={I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.generate_bearer_token.label')}>
            <Token bearerToken={bearerToken} />
          </Cell>}
        <Cell hasBorder={!hasToken} align="right">
          <Actions>
            {hasToken && <ClipboardButton decoration="outline" size="small" content={bearerToken.token}>
                {I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.bearer_token.copy')}
              </ClipboardButton>}
            <RevokeTokenPopup isOpen={isOpen} onClose={closePopup} onRevoke={handleRevoke} />
            <DangerButton onClick={openPopup}>
              {I18n.t('publisher.organization_settings.user_provisioning.scim_configuration.bearer_token.revoke_copy')}
            </DangerButton>
          </Actions>
        </Cell>
      </Row>
      {hasToken && isDesktopUp && <Row>
          <DesktopCell hasBorder colSpan={3}>
            <Token bearerToken={bearerToken} />
          </DesktopCell>
        </Row>}
    </>;
};

require("./token_row.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./token_row.js");